import React, { useEffect, useState } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
} from "recharts";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { StressSituationActionCreators } from "redux/rootActions";
import { stressSituationDataResponse } from "redux/stressCheck/stressSituation/stressSituationStates";

import { Loading } from "components";
import {
  BoxContent,
  WrapperChart,
} from "screens/bodyTemperature/BodyTemperature.style";
import { Square, ContainerChart } from "../Stress.style";
import {
  WrapperLoadingChart,
  WrapperLegendLineChart,
} from "screens/dashboard/Dashboard.style";

interface TooltipItem {
  dot: any;
  label: string;
}

interface BarChartProps {
  data: any;
}

const datas = [
  {
    date: "2022/01",
    noStress: 52,
    highStress: 22,
  },
  {
    date: "2022/02",
    noStress: 68,
    highStress: 13,
  },
  {
    date: "2022/03",
    noStress: 54,
    highStress: 25,
  },
  {
    date: "2022/04",
    noStress: 55,
    highStress: 16,
  },
  {
    date: "2022/05",
    noStress: 58,
    highStress: 22,
  },
  {
    date: "2022/06",
    noStress: 52,
    highStress: 22,
  },
  {
    date: "2022/07",
    noStress: 68,
    highStress: 13,
  },
  {
    date: "2022/08",
    noStress: 54,
    highStress: 25,
  },
  {
    date: "2022/09",
    noStress: 55,
    highStress: 16,
  },
  {
    date: "2022/10",
    noStress: 58,
    highStress: 22,
  },
  {
    date: "2022/11",
    noStress: 55,
    highStress: 16,
  },
  {
    date: "2022/12",
    noStress: 58,
    highStress: 22,
  },
];

const returnItem = (name: string) => {
  let item: TooltipItem = {
    dot: <></>,
    label: "",
  };
  switch (name) {
    case "noStress":
      item = {
        dot: <Square style={{ background: "#2F8CAE" }} />,
        label: "không Stress",
      };
      break;
    case "highStress":
      item = {
        dot: <Square style={{ background: "#FB4E4E" }} />,
        label: "Stress cao",
      };
      break;
  }

  return item;
};

const CustomizedXAxisTick = (props: any) => {
  const { x, y, payload } = props;

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={-23} y={0} dy={8} fill="#3B3B3B">
        {payload.value}
      </text>
    </g>
  );
};

const CustomizedYAxisTick = (props: any) => {
  const { x, y, payload } = props;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={3} fill="#3B3B3B" textAnchor="end">
        {payload.value} {payload.value === 100 && "%"}
      </text>
    </g>
  );
};

const BarChartCustom = ({ data }: BarChartProps) => {
  const renderTooltipContent = (props: any) => {
    const { payload, label } = props;

    return (
      <div className="customized-tooltip-content">
        <p className="label">{label}</p>
        {payload && (
          <div className="list">
            {payload.map((entry: any, index: any) => (
              <div key={`item-${index}`} className="tooltip-item-wrapper">
                <div className="item-left">
                  {returnItem(entry.name)?.dot}
                  <span>{returnItem(entry.name)?.label}</span>
                </div>
                <div className="item-right">
                  <span>{entry.value}%</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <ContainerChart>
      <ResponsiveContainer width="100%" height={168}>
        <BarChart
          margin={{
            top: 10,
            right: 20,
            left: 0,
            bottom: 0,
          }}
          data={data}
        >
          <CartesianGrid stroke="#2F8CAE" />
          <XAxis
            dy={4}
            tickLine={false}
            dataKey="date"
            minTickGap={-100}
            tick={<CustomizedXAxisTick />}
            stroke="#CCCCCC"
          />
          <YAxis
            type="number"
            tickLine={false}
            interval={0}
            width={55}
            tick={<CustomizedYAxisTick />}
            stroke="#CCCCCC"
            domain={[0, 100]}
            minTickGap={25}
          />
          <Bar
            dataKey="noStress"
            barSize={44}
            stackId="noStress"
            fill="#2F8CAE"
          />
          <Bar
            dataKey="highStress"
            stackId="noStress"
            fill="#FB4E4E"
            barSize={44}
          />
          <Tooltip content={renderTooltipContent} />
        </BarChart>
      </ResponsiveContainer>
    </ContainerChart>
  );
};

const StressTransition = () => {
  const { t }: any = useTranslation("translation", {
    keyPrefix: "stress-check",
  });
  const { t: t1 }: any = useTranslation("translation", {
    keyPrefix: "mental-health",
  });

  const dispatch = useDispatch();
  const stressSituationData = useSelector(stressSituationDataResponse);

  const [data, setData] = useState([]);

  useEffect(() => {
    dispatch(StressSituationActionCreators.stressSituationAction());
  }, []);

  useEffect(() => {
    if (stressSituationData && Object.keys(stressSituationData).length) {
      setData(
        stressSituationData.result.map((item: any) => {
          return {
            date: item.year_month,
            noStress: item.mini_stress_check_value,
            highStress: item.full_stress_check_value,
          };
        })
      );
    }
  }, [stressSituationData]);

  const chartItems = [
    {
      id: 0,
      label: t("no-stress"),
      color: "#2F8CAE",
    },
    {
      id: 1,
      label: t("high-stress"),
      color: "#FB4E4E",
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          fontWeight: 700,
          fontSize: 16,
          lineHeight: "16px",
          marginBottom: 6,
        }}
      >
        {t("stress-situation-transition")}
        <span
          style={{
            fontWeight: 400,
            fontSize: 12,
            lineHeight: "14px",
            color: "#666666",
          }}
        >
          {t("last-12-months")}
        </span>
      </div>
      {datas.length > 0 ? (
        <BoxContent>
          <WrapperChart>
            <ContainerChart>
              <div
                className="icon-descriptions"
                style={{
                  justifyContent: "flex-end",
                  marginBottom: 0,
                  marginRight: 19,
                }}
              >
                {chartItems && (
                  <div
                    className="icon-descriptions-stacked"
                    style={{
                      display: "flex",
                      height: "max-content",
                    }}
                  >
                    {chartItems.map((item: any) => (
                      <div className="description" key={item.id}>
                        <WrapperLegendLineChart>
                          <Square
                            style={{ background: item.color, marginTop: 0 }}
                          />
                          <p>{item.label}</p>
                        </WrapperLegendLineChart>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </ContainerChart>
            <BarChartCustom data={data} />
          </WrapperChart>
        </BoxContent>
      ) : (
        <WrapperLoadingChart>
          <div className="container">
            <Loading width={70} height={70} />
          </div>
        </WrapperLoadingChart>
      )}
    </div>
  );
};

export default StressTransition;
